import { formatDollarsAsUsd } from "@src/appV2/lib/Money/utils/currency";

import { type Faq } from "../components/TaxationFaqs";

interface GetFaqsWhenIneligibleToPayTaxParams {
  formIssuanceThresholdInDollars: number;
  taxationYear: number;
}

export function getFaqsWhenIneligibleToPayTax(params: GetFaqsWhenIneligibleToPayTaxParams): Faq[] {
  const { formIssuanceThresholdInDollars, taxationYear } = params;

  return [
    {
      question: "Do I have to pay taxes?",
      answer: [
        {
          message: `Since you work as an independent contractor through Clipboard, Clipboard doesn't take taxes out of your paycheck like a regular job does. This means you may need to pay taxes yourself when you file your tax return each year. Users who earned less than ${formatDollarsAsUsd(
            formIssuanceThresholdInDollars,
            { maximumFractionDigits: 0 }
          )} will not receive a 1099-NEC form from Clipboard.`,
        },
      ],
    },
    {
      question: "What's a 1099-NEC form?",
      answer: [
        {
          message: `A 1099-NEC is a form that shows how much money you earned working on Clipboard. You'll need this form to file your taxes. You only get one if you earned ${formatDollarsAsUsd(
            formIssuanceThresholdInDollars,
            { maximumFractionDigits: 0 }
          )} or more in ${taxationYear}.`,
        },
      ],
    },
    {
      question: "Will I get a tax form?",
      answer: [
        {
          message: `If you earned ${formatDollarsAsUsd(formIssuanceThresholdInDollars, {
            maximumFractionDigits: 0,
          })} or more on Clipboard in ${taxationYear}, you'll get a 1099-NEC form. If you earned less than ${formatDollarsAsUsd(
            formIssuanceThresholdInDollars,
            { maximumFractionDigits: 0 }
          )}, you won't get one. You can check back to this page in January to see your total ${taxationYear} earnings.`,
        },
      ],
    },
    {
      question: "How will I get my tax form?",
      answer: [
        {
          message: `Your 1099-NEC form will be sent to you electronically. 

To ensure smooth delivery, watch for an email from Stripe asking you to opt in to electronic delivery.

Once you receive this email, and log in to Stripe, make sure everything is updated:

- Your information matches what you told Clipboard (name, address)
- Your Social Security Number is correct
- Do this BEFORE January so it shows up correctly on your tax form

Note that forms will not be available until Jan 31, ${taxationYear + 1}.`,
        },
      ],
    },
    {
      question: "Why do I need to give my Social Security Number?",
      answer: [
        {
          message:
            "The IRS needs your Social Security Number (or EIN for businesses) to file your tax form with the government.",
        },
      ],
    },
    {
      question: "I didn't get the email from Stripe",
      answer: [
        {
          message: `Check these things first:

- Look in your spam or deleted email folders
- Make sure your email address is correct in your Stripe account
- Check if you actually earned ${formatDollarsAsUsd(formIssuanceThresholdInDollars, {
            maximumFractionDigits: 0,
          })} or more in ${taxationYear}

If you did all that and still didn't get the email, contact Clipboard support through the app (Account > Get Help > Contact Support).`,
        },
      ],
    },
    {
      question: "I can't log in to my Stripe Express account",
      answer: [
        {
          title: "My phone number changed or I lost it",
          message: `Don't worry! You can still log in and update your number. Here's how:
1. Go to express.stripe.com
2. Enter the email address for your account
3. When it asks for a 6-digit verification code, look for the option that says "I no longer have access to this phone number"
4. Click it
5. Follow the steps to verify it's really you
6. Add your new phone number
7. You're back in!
`,
        },
        {
          title: "Other login problems",
          message:
            "If you're having trouble logging in for any other reason, contact Stripe support directly. They can help get you back into your account.",
        },
      ],
    },
    {
      question: "Can Clipboard tell me how many miles I drove for shifts this year?",
      answer: [
        {
          message: `Yes, we will send you a log of your ${taxationYear} shifts with estimated mileage via email the first week of January.`,
        },
      ],
    },
  ];
}
